import type { PageAgentViewer } from '../hooks/usePageAgentViewers.ts';

import type { PagePerson, PagePersonRelationshipEvidence } from './parsePagePeople.ts';
import type { RankedPagePerson } from './rankPagePeople.ts';
import { PAGE_PEOPLE_RELATIONSHIPS } from './pagePeopleRelationships.ts';

export type RankedPagePersonOrAgent = RankedPagePerson & { agent?: PageAgentViewer };

type PagePersonCandidate = {
	person: PagePerson;
	agent: PageAgentViewer | undefined;
	primary: PagePersonRelationshipEvidence | undefined;
};

const EXCLUDED_RELATIONSHIP_PRIORITY = 4;

const relationshipOrder = new Map(
	PAGE_PEOPLE_RELATIONSHIPS.map(({ relationship }, index) => [relationship, index]),
);

const priority = (
	relationship: PagePersonRelationshipEvidence['relationship'],
	isAgent: boolean,
): number => {
	switch (relationship) {
		case 'owns':
			return 0;
		case 'created':
		case 'contributed':
		case 'updated':
			return 1;
		case 'viewed':
			return isAgent ? 2 : EXCLUDED_RELATIONSHIP_PRIORITY;
		case 'mentioned-in':
			return 3;
		default:
			return EXCLUDED_RELATIONSHIP_PRIORITY;
	}
};

// Graph timestamps are relationship recency evidence, not necessarily individual action times.
const timestamp = (value: number | null): number =>
	value != null && Number.isFinite(value) ? value : -Infinity;

const compareText = (left: string, right: string): number =>
	left < right ? -1 : left > right ? 1 : 0;

const compareRelationships = (
	left: PagePersonRelationshipEvidence,
	right: PagePersonRelationshipEvidence,
	isAgent: boolean,
): number =>
	priority(left.relationship, isAgent) - priority(right.relationship, isAgent) ||
	timestamp(right.lastUpdated) - timestamp(left.lastUpdated) ||
	(relationshipOrder.get(left.relationship) ?? Number.MAX_SAFE_INTEGER) -
		(relationshipOrder.get(right.relationship) ?? Number.MAX_SAFE_INTEGER);

const pickPrimaryRelationship = (
	person: PagePerson,
	isAgent: boolean,
): PagePersonRelationshipEvidence | undefined => {
	let primary: PagePersonRelationshipEvidence | undefined;
	for (const relationship of person.relationships) {
		if (!primary || compareRelationships(relationship, primary, isAgent) < 0) {
			primary = relationship;
		}
	}
	return primary;
};

const lastViewedTimestamp = (agent: PageAgentViewer): number | null => {
	const timestamp = agent.lastViewedAt ? Date.parse(agent.lastViewedAt) : NaN;
	return Number.isFinite(timestamp) ? timestamp : null;
};

/** Rank section candidates by role, then recency. The canvas keeps its own ranking. */
export const rankPagePeopleAndAgents = (
	people: readonly PagePerson[],
	agents: readonly PageAgentViewer[],
): RankedPagePersonOrAgent[] => {
	const agentsByIdentity = new Map<string, PageAgentViewer>();
	for (const agent of agents) {
		// Analytics IDs can include an attributed user's ID; the identity is the agent itself.
		// Unresolved agents retain their source row ID rather than guessing from a display name.
		const identity = agent.identityAccountId || agent.id;
		const existing = agentsByIdentity.get(identity);
		if (!existing) {
			agentsByIdentity.set(identity, agent);
			continue;
		}
		const latest =
			(lastViewedTimestamp(agent) ?? -Infinity) > (lastViewedTimestamp(existing) ?? -Infinity)
				? agent
				: existing;
		agentsByIdentity.set(identity, {
			...existing,
			lastViewedAt: latest.lastViewedAt,
			avatarURL: existing.avatarURL || agent.avatarURL,
		});
	}

	const agentsByKey = new Map<string, PageAgentViewer>();
	const agentCandidates: PagePerson[] = [];
	for (const [identity, agent] of agentsByIdentity) {
		// A local row key, never a graph ARI or a navigation target.
		const key = `agent:${identity}`;
		agentsByKey.set(key, agent);
		agentCandidates.push({
			ari: key,
			accountId: agent.identityAccountId ?? '',
			name: agent.name,
			picture: agent.avatarURL || null,
			relationships: [
				{ relationship: 'viewed', direction: 'incoming', lastUpdated: lastViewedTimestamp(agent) },
			],
		});
	}

	const candidates: PagePersonCandidate[] = [...people, ...agentCandidates].map((person) => {
		const agent = agentsByKey.get(person.ari);
		return { person, agent, primary: pickPrimaryRelationship(person, !!agent) };
	});
	const eligiblePeople = candidates.filter(
		(candidate): candidate is PagePersonCandidate & { primary: PagePersonRelationshipEvidence } =>
			!!candidate.primary &&
			priority(candidate.primary.relationship, !!candidate.agent) < EXCLUDED_RELATIONSHIP_PRIORITY,
	);

	return eligiblePeople
		.sort(
			(left, right) =>
				priority(left.primary.relationship, !!left.agent) -
					priority(right.primary.relationship, !!right.agent) ||
				timestamp(right.primary.lastUpdated) - timestamp(left.primary.lastUpdated) ||
				compareText(left.person.ari, right.person.ari) ||
				compareText(left.person.accountId, right.person.accountId),
		)
		.map(({ person, primary, agent }) => ({
			ari: person.ari,
			accountId: person.accountId,
			name: person.name,
			picture: person.picture,
			...(person.jobTitle ? { jobTitle: person.jobTitle } : {}),
			primaryRelationship: primary.relationship,
			relationshipDirection: primary.direction,
			...(agent ? { agent } : {}),
		}));
};
